import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import { type Asset } from '@/api/generated/model';
import { GoalTemplatePicker } from '@/components/goals/GoalTemplatePicker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { buildPlannedGoalFromTemplate, type GoalTemplateId } from '@/features/goals/goalTemplates';
import { usePlansList } from '@/features/planning';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import type { PlannedGoal } from '@/services/goalPlanningStorage';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const goalSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  targetAmount: z.coerce.number().min(0, 'Target amount must be positive'),
  currentAmount: z.coerce.number().min(0, 'Current amount must be 0 or greater'),
  dueDate: z.date({
    required_error: 'Due date is required',
  }),
  priority: z.enum(['mandatory', 'nice-to-have'], {
    required_error: 'Priority is required',
  }),
});

const formSchema = z.object({
  goals: z.array(goalSchema).min(1, 'At least one goal is required'),
});

type FormValues = z.infer<typeof formSchema>;

interface GoalCreationStepProps {
  initialGoals?: PlannedGoal[];
  currency: string;
  assets: Asset[];
  isLoadingAssets: boolean;
  onSubmit: (goals: PlannedGoal[]) => void;
}

export const GoalCreationStep = ({ initialGoals, currency, onSubmit }: GoalCreationStepProps) => {
  const { t } = useLanguage();
  const { plans } = usePlansList();
  const { data: accountsResponse } = useGetAllAccounts();
  const accounts = accountsResponse?.items ?? [];
  const defaultAccountId = accounts.find((a) => a.type !== 'liability')?.id;

  const [goals, setGoals] = useState<Partial<PlannedGoal>[]>(
    initialGoals && initialGoals.length > 0
      ? initialGoals
      : [
          {
            id: Date.now().toString(),
            name: '',
            targetAmount: 0,
            currentAmount: 0,
            currency,
            dueDate: new Date(new Date().getFullYear() + 1, 0, 1).toISOString(), // Next year Jan 1
            priority: 'nice-to-have',
            rank: 1,
            monthlyAllocation: 0,
            yearlyAllocation: 0,
          },
        ]
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      goals: goals.map((g) => ({
        name: g.name || '',
        description: g.description || '',
        targetAmount: g.targetAmount || 0,
        currentAmount: g.currentAmount || 0,
        dueDate: g.dueDate ? new Date(g.dueDate) : new Date(new Date().getFullYear() + 1, 0, 1),
        priority: (g.priority as 'mandatory' | 'nice-to-have') || 'nice-to-have',
      })),
    },
  });

  const addGoal = () => {
    const newGoal: Partial<PlannedGoal> = {
      id: Date.now().toString(),
      name: '',
      targetAmount: 0,
      currentAmount: 0,
      currency,
      dueDate: new Date(new Date().getFullYear() + 1, 0, 1).toISOString(),
      priority: 'nice-to-have',
      rank: goals.length + 1,
      monthlyAllocation: 0,
      yearlyAllocation: 0,
    };
    setGoals([...goals, newGoal]);
    form.setValue('goals', [
      ...form.getValues('goals'),
      {
        name: '',
        description: '',
        targetAmount: 0,
        currentAmount: 0,
        dueDate: new Date(new Date().getFullYear() + 1, 0, 1),
        priority: 'nice-to-have',
      },
    ]);
  };

  const removeGoal = (index: number) => {
    const newGoals = goals.filter((_, i) => i !== index);
    setGoals(newGoals);
    form.setValue(
      'goals',
      form.getValues('goals').filter((_, i) => i !== index)
    );
  };

  const handleTemplateSelect = (templateId: GoalTemplateId) => {
    const planned = buildPlannedGoalFromTemplate(
      templateId,
      { currency, defaultAccountId, plans },
      t,
      goals.length + 1
    );
    const nextGoals = [...goals, planned];
    setGoals(nextGoals);
    form.setValue('goals', [
      ...form.getValues('goals'),
      {
        name: planned.name,
        description: planned.description || '',
        targetAmount: planned.targetAmount,
        currentAmount: planned.currentAmount,
        dueDate: new Date(planned.dueDate),
        priority: planned.priority,
      },
    ]);
  };

  const handleSubmit = (data: FormValues) => {
    const plannedGoals: PlannedGoal[] = data.goals.map((goal, index) => {
      const stored = goals[index];
      return {
        id: stored?.id || Date.now().toString() + index,
        name: goal.name,
        description: goal.description,
        targetAmount: goal.targetAmount,
        currentAmount: goal.currentAmount,
        currency,
        dueDate: goal.dueDate.toISOString(),
        priority: goal.priority,
        rank: index + 1,
        monthlyAllocation: stored?.monthlyAllocation ?? 0,
        yearlyAllocation: stored?.yearlyAllocation ?? 0,
        goalType: stored?.goalType,
        templateParams: stored?.templateParams,
      };
    });

    onSubmit(plannedGoals);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
        <GoalTemplatePicker onSelect={handleTemplateSelect} className='pb-2' />
        <div className='space-y-4'>
          {form.watch('goals').map((_, index) => (
            <div key={index} className='space-y-4 rounded-lg border p-4'>
              <div className='flex items-center justify-between'>
                <h3 className='font-medium'>
                  {t('goalPlanning.goalCreation.goal')} {index + 1}
                </h3>
                {form.watch('goals').length > 1 && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => removeGoal(index)}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                )}
              </div>

              <FormField
                control={form.control}
                name={`goals.${index}.name`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('goals.name')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('goals.form.namePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`goals.${index}.description`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('goals.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('goals.form.descriptionPlaceholder')}
                        className='resize-none'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name={`goals.${index}.targetAmount`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('goals.targetAmount')}</FormLabel>
                      <FormControl>
                        <Input
                          type='number'
                          step='0.01'
                          placeholder={t('goals.form.targetAmountPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`goals.${index}.currentAmount`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('goals.currentAmount')}</FormLabel>
                      <FormControl>
                        <Input type='number' step='0.01' placeholder='0.00' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name={`goals.${index}.dueDate`}
                  render={({ field }) => (
                    <FormItem className='flex flex-col'>
                      <FormLabel>{t('goals.dueDate')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant='outline'
                              className={cn(
                                'w-full pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'PPP')
                              ) : (
                                <span>{t('goals.form.dueDatePlaceholder')}</span>
                              )}
                              <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='w-auto p-0' align='start'>
                          <Calendar
                            mode='single'
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`goals.${index}.priority`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('goals.priority')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('goals.form.priorityPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='mandatory'>
                            {t('goals.priorities.mandatory')}
                          </SelectItem>
                          <SelectItem value='nice-to-have'>
                            {t('goals.priorities.nice-to-have')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          ))}
        </div>

        <Button type='button' variant='outline' onClick={addGoal} className='w-full'>
          <Plus className='mr-2 h-4 w-4' />
          {t('goalPlanning.goalCreation.addGoal')}
        </Button>

        <Button type='submit' className='w-full'>
          {t('goalPlanning.goalCreation.continue')}
        </Button>
      </form>
    </Form>
  );
};
